/**
 * A helper method for loading a chain of scripts asynchronously without allowing the scripts within the chain to load out of order
 *
 * @param scriptURLs a list of valid script tag src attribute values
 * @param initialPromise - an optional Promise to which the script-loading Promises should be attached
 * @return a Promise that will become resolved iff the last script has been loaded (or an error occurred while loading the promises)
 */
export default function loadScripts(scriptURLs: string[],
									initialPromise?: Promise<boolean>
) {
	const loadScript = (scriptSource: string) => () => new Promise<boolean>((resolve, reject) => {
		const timeoutId = setTimeout(() => {
			reject(`Timed out while waiting for ${scriptSource} to load`);
		}, 30_000);

		const script = document.createElement("script");
		script.addEventListener("load", () => {
			try {
				clearTimeout(timeoutId);
			} finally {
				resolve(true);
			}
		});
		script.addEventListener("error", event => {
			try {
				clearTimeout(timeoutId);
			} finally {
				reject(event.error);
			}
		});
		script.type = "text/javascript";
		script.async = true;
		script.defer = true;
		script.src = scriptSource;
		document.head.appendChild(script);
	});
	//(reduce: (a: string, b: string) => a + b) => string
	//(reduce: (a: container, b: string) => a.add(b)) => container
	return scriptURLs
		.reduce(
			(promise, scriptURL) => promise.then(loadScript(scriptURL)),
			initialPromise ?? Promise.resolve(true)
		);
}
